<script setup lang="ts">
import {PropType, ref, useAttrs, reactive} from "vue";
import {odsPill, PillTheme} from "../../components/odsPill";
import {odsFieldMessage} from "../../components/odsFieldMessage/";
import { IconName } from "../../components/odsIcon";
import {Spacing} from "../../tokens";

const emit = defineEmits(['mouseover', 'mouseout', 'blur', 'focus', 'change'])

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  defaultValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  messageTheme: {
    type: String as PropType<typeof PillTheme[keyof typeof PillTheme]>,
    default: PillTheme.Default,
  },
  validators: {
    type: Array as PropType<{ label: string; theme: (value: string) => PillTheme; }[]>,
    default: [],
  },
  padding: {
    type: String as PropType<typeof Spacing[keyof typeof Spacing]>,
    default: Spacing.Standard,
  },
  maxlength: {
    type: Number,
    default: Number.MAX_SAFE_INTEGER
  }
});

const value = ref(props.defaultValue);

const input = ref();

const attrs = reactive(
    {
        ...useAttrs(),
        onFocus: (event : Event) => focusInput(event),
        onBlur: (event : Event) => blurInput(event),
        onMouseover: (event : Event) => mouseover(event),
        onMouseout: (event : Event) => mouseout(event)
    }
)

function getValidatorIcon(theme) {
  switch (theme) {
    case PillTheme.Success:
      return IconName.Check;
    case PillTheme.Warning:
      return IconName.Exclamation;
    case PillTheme.Failure:
      return IconName.Close;
    default:
      return undefined;
  }
}

function mouseover(event : Event){
    emit('mouseover', event)
}
function mouseout(event : Event){
    emit('mouseout', event)        
}
function blurInput(event : Event){
    emit('blur',event)
}
function focusInput(event : Event){
    emit('focus', event)
}
</script>

<template>
  <div
      class="ods-textarea"
      :class="[{disabled, error: messageTheme === PillTheme.Failure, warning: messageTheme === PillTheme.Warning}]"
  >
    <label v-if="label" v-html="label" :for="id"/>
    <div class="ods-textarea-wrapper">
      <textarea
        v-bind="attrs" 
        :id="id"
        :class="[{ disabled }]"
        :placeholder="placeholder"
        :disabled="disabled"
        :maxlength="maxlength"
        v-model="value"
        ref="textarea"
        @change.stop="$emit('change', value)"
      ></textarea>
      <span class="ods-char-counter" v-if="maxlength !== Number.MAX_SAFE_INTEGER">
        {{ value.length || 0 }} / {{ maxlength }}
      </span>
    </div>
    <div v-if="validators.length > 0" class="conditions">
      <odsPill v-for="(validator, index) of validators" :key="index" :theme="validator.theme(value)"
        :icon="getValidatorIcon(validator.theme(value))" :label="validator.label" />
      />
    </div>
    <odsFieldMessage
        :message="message"
        :message-theme="messageTheme"
    />
  </div>
</template>

<style lang="scss" src="./odsTextarea.scss"/>